import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from 'react';

type Difficulty = 'Easy Mode' | 'Normal Mode';

interface DifficultyContextType {
  difficulty: Difficulty;
  toggleDifficulty: () => void;
  setDifficulty: (difficulty: Difficulty) => void;
}

const DifficultyContext = createContext<DifficultyContextType | undefined>(undefined);

interface DifficultyProviderProps {
  children: ReactNode;
}

interface ControlledDifficultyProviderProps extends DifficultyProviderProps {
  value: DifficultyContextType;
}

export const ControlledDifficultyProvider: React.FC<ControlledDifficultyProviderProps> = ({
  children,
  value,
}) => <DifficultyContext.Provider value={value}>{children}</DifficultyContext.Provider>;

// External store implementation
const difficultyStore = {
  listeners: new Set<() => void>(),

  subscribe(callback: () => void) {
    difficultyStore.listeners.add(callback);
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', callback);
    }
    return () => {
      difficultyStore.listeners.delete(callback);
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', callback);
      }
    };
  },

  getSnapshot(): Difficulty {
    if (typeof window === 'undefined') return 'Normal Mode';

    try {
      const savedDifficulty = localStorage.getItem('difficulty') as Difficulty | null;
      return savedDifficulty &&
        (savedDifficulty === 'Easy Mode' || savedDifficulty === 'Normal Mode')
        ? savedDifficulty
        : 'Normal Mode';
    } catch {
      return 'Normal Mode';
    }
  },

  getServerSnapshot(): Difficulty {
    return 'Normal Mode';
  },

  setDifficulty(newDifficulty: Difficulty) {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('difficulty', newDifficulty);
      difficultyStore.emitChange();
    } catch (e) {
      console.warn('Failed to write difficulty to localStorage', e);
    }
  },

  emitChange() {
    for (const listener of difficultyStore.listeners) {
      listener();
    }
  },
};

export const DifficultyProvider: React.FC<DifficultyProviderProps> = ({ children }) => {
  const difficulty = useSyncExternalStore(
    difficultyStore.subscribe,
    difficultyStore.getSnapshot,
    difficultyStore.getServerSnapshot
  );

  // Sync document body attribute as a side effect of state change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.body.setAttribute('data-difficulty', difficulty);
    }
  }, [difficulty]);

  const toggleDifficulty = useCallback(() => {
    // Read current state from store to ensure freshness
    const current = difficultyStore.getSnapshot();
    const next = current === 'Easy Mode' ? 'Normal Mode' : 'Easy Mode';
    difficultyStore.setDifficulty(next);
  }, []);

  const setDifficulty = useCallback((newDifficulty: Difficulty) => {
    difficultyStore.setDifficulty(newDifficulty);
  }, []);

  const contextValue = useMemo(
    () => ({
      difficulty,
      toggleDifficulty,
      setDifficulty,
    }),
    [difficulty, toggleDifficulty, setDifficulty]
  );

  return (
    <ControlledDifficultyProvider value={contextValue}>{children}</ControlledDifficultyProvider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useDifficulty = (): DifficultyContextType => {
  const context = useContext(DifficultyContext);
  if (context === undefined) {
    throw new Error('useDifficulty must be used within a DifficultyProvider');
  }
  return context;
};
